import { useState } from "react";
import { ChevronDown, Layers3, Lock, Sparkles, Ticket, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fmtOdds, pct, SPORT_META, CONF_STYLE } from "@/lib/odds";
import { useSlip } from "@/context/SlipContext";
import { MoveChip } from "@/components/MoveChip";
import { PlayerLink } from "@/components/PlayerLink";

export function SGPCard({ sgp, index = 0 }) {
  const [open, setOpen] = useState(false);
  const { replace } = useSlip();
  const meta = SPORT_META[sgp.sport];
  const time = new Date(sgp.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  const load = () => {
    replace(sgp.legs, { title: sgp.title, strategy: sgp.reasoning });
    toast.success(`"${sgp.title}" loaded into your slip`);
  };

  return (
    <article data-testid="sgp-card" className="glass-card fade-up flex flex-col gap-4 p-5" style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="label-caps flex items-center gap-1.5" style={{ color: meta.color }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />{meta.short}</span>
            <span className="text-xs text-slate-500">· {time}</span>
            <span className="flex items-center gap-1 rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300"><Layers3 size={10} /> {sgp.sport === "horse_racing" ? "Exotic" : "Same Game"}</span>
          </div>
          <p className="mt-1.5 truncate text-sm text-slate-400" data-testid="sgp-matchup">{sgp.matchup}</p>
          <h3 className="font-display mt-1 text-2xl font-bold leading-none tracking-tight text-white" data-testid="sgp-title">{sgp.title}</h3>
          <p className="mt-1.5 text-[11px] text-slate-500">{sgp.legs.filter((l) => l.market === "Player Prop" || l.market === "Alt Line").length} player props · {sgp.legs.filter((l) => l.market === "Alt Line").length} alt lines</p>
        </div>
        <div className="text-right">
          <p className="font-mono-num text-2xl font-bold text-amber-400" data-testid="sgp-odds">{fmtOdds(sgp.combined_american)}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{sgp.legs.length} legs</p>
        </div>
      </div>

      <ul className="divide-y divide-[#26334D]/60 rounded-lg border border-[#26334D] bg-[#0B0E14]/60" data-testid="sgp-legs">
        {sgp.legs.map((l) => (
          <li key={l.pick_id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${l.market === "Alt Line" ? "bg-fuchsia-500/20 font-bold text-fuchsia-300" : "bg-[#182030] text-slate-400"}`} data-testid={l.market === "Alt Line" ? "sgp-alt-leg" : undefined}>{l.market === "Alt Line" ? "ALT" : l.market}</span>
            <span className="min-w-0 flex-1 truncate font-semibold text-white"><PlayerLink player={l.player} label={l.selection} /></span>
            <MoveChip movement={l.movement} compact />
            <span className="font-mono-num text-amber-400">{fmtOdds(l.odds)}{l.odds_estimated && <span className="ml-1 text-[9px] uppercase tracking-wider text-slate-500">est.</span>}</span>
          </li>
        ))}
      </ul>
      {sgp.trifecta && (
        <div data-testid="sgp-trifecta" className="flex items-center gap-3 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 px-3 py-2 text-sm">
          <span className="rounded bg-fuchsia-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-300">Trifecta box</span>
          <span className="min-w-0 flex-1 truncate font-semibold text-white">{sgp.trifecta.selection.replace("Trifecta Box: ", "")}</span>
          <span className="text-[10px] text-slate-500">{pct(sgp.trifecta.win_probability)}</span>
          <span className="font-mono-num text-amber-400">{fmtOdds(sgp.trifecta.odds)}{sgp.trifecta.odds_estimated && <span className="ml-1 text-[9px] uppercase tracking-wider text-slate-500">est.</span>}</span>
        </div>
      )}

      <div className="flex items-center justify-between rounded-lg bg-[#0B0E14] px-3 py-2 text-xs">
        <span className="text-slate-500">Correlated hit rate <span data-testid="sgp-win-prob" className="font-mono-num font-bold text-emerald-400">{pct(sgp.win_probability)}</span></span>
        <span className="flex items-center gap-1.5">
          {sgp.consensus && <span data-testid="sgp-consensus" title={`${sgp.consensus.shared_legs} leg(s) also chosen by ${sgp.consensus.models?.[1]}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${sgp.consensus.agree ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400"}`}><ShieldCheck size={10} /> {sgp.consensus.agree ? "Consensus" : `${sgp.consensus.shared_legs} shared`}</span>}
          <span data-testid="sgp-confidence" className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CONF_STYLE[sgp.confidence] || CONF_STYLE.medium}`}>{sgp.confidence}</span>
        </span>
      </div>

      <div>
        <button data-testid="sgp-reasoning-toggle" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wider text-slate-400 transition-colors hover:text-emerald-400">
          <span className="flex items-center gap-1.5"><Sparkles size={12} className="text-cyan-400" /> Game script</span><ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        {open && <p className="fade-up mt-3 text-sm leading-relaxed text-slate-300" data-testid="sgp-reasoning">{sgp.reasoning}</p>}
      </div>

      <button data-testid="sgp-load-slip-btn" onClick={load} className="btn-primary w-full"><Ticket size={16} /> Load into Slip</button>
    </article>
  );
}

export function SGPLockedCard({ locked }) {
  return (
    <div data-testid="sgp-locked-card" className="glass-card flex flex-col items-center justify-center gap-3 border-dashed border-amber-500/40 p-8 text-center">
      <Lock size={22} className="text-amber-400" />
      <p className="text-sm text-slate-300"><span className="font-bold text-white">{locked} more</span> same game parlays today are on Basic & Premium.</p>
      <a href="/pricing" data-testid="sgp-upgrade-link" className="btn-ghost !py-2 text-xs">See plans</a>
    </div>
  );
}
